import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import usePageContext from "./hooks/usePageContext";
import useSuggestions from "./hooks/useSuggestions";
import { sendMessage, saveFeedback, getConversation } from "./api/chatClient";

import Composer from "./components/Composer";
import MessageList from "./components/MessageList";
import { RemiMark, ChevronDownIcon, CloseIcon } from "./icons";
import "./remi.css";

const OPEN_STATE_KEY = "remi.open";

// Routes where Remi must not appear.
const HIDDEN_ROUTES = [/^\/$/, /^\/locations/i];

/**
 * ChatMS v1 has no streaming: the agent loop may call three or four billing APIs
 * before it can answer, so responses land whole after 5-20s. A bare spinner for
 * 15 seconds reads as a hung app, so we narrate the wait instead.
 */
const PROGRESS_STEPS = [
  { at: 0, label: "Reading your question…" },
  { at: 2500, label: "Looking up claim data…" },
  { at: 7000, label: "Checking denial codes and policies…" },
  { at: 13000, label: "Putting the answer together…" },
  { at: 22000, label: "Still working — complex questions take longer…" },
];

let idCounter = 0;
const nextId = () => `remi-${Date.now().toString(36)}-${(idCounter += 1)}`;

function readOpenState() {
  try {
    return localStorage.getItem(OPEN_STATE_KEY) === "true";
  } catch {
    return false;
  }
}

export default function RemiWidget() {
  const userData = useSelector((state) => state.user.user);
  const { pathname } = useLocation();
  const { practiceID, userID, pageContext, sessionContext } = usePageContext();

  const [open, setOpen] = useState(readOpenState);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conversationID, setConversationID] = useState(null);

  const { suggestions, isPersonalised, recentTopics } = useSuggestions({
    open,
    practiceID,
    userID,
    pageContext,
  });

  const bodyRef = useRef(null);
  const abortRef = useRef(null);
  const launcherRef = useRef(null);
  const panelRef = useRef(null);
  const progressTimers = useRef([]);

  const firstName = (userData?.firstName || userData?.FirstName || "").trim();

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_STATE_KEY, String(open));
    } catch {
      /* private browsing / quota */
    }
  }, [open]);

  // Conversation content is never persisted client-side - it is PHI. The server
  // holds history under a retention policy; we re-fetch rather than cache.
  useEffect(() => {
    if (!open) return;
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  // Esc closes; focus returns to the launcher.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    if (open) panelRef.current?.focus?.();
    else launcherRef.current?.focus?.();
  }, [open]);

  useEffect(
    () => () => {
      abortRef.current?.abort();
      progressTimers.current.forEach(clearTimeout);
    },
    []
  );

  const patchMessage = useCallback((id, patch) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === "function" ? patch(m) : patch) } : m))
    );
  }, []);

  const startProgressNarration = useCallback(
    (messageId) => {
      progressTimers.current.forEach(clearTimeout);
      progressTimers.current = PROGRESS_STEPS.map((step) =>
        setTimeout(() => patchMessage(messageId, { statusLabel: step.label }), step.at)
      );
    },
    [patchMessage]
  );

  const stopProgressNarration = useCallback(() => {
    progressTimers.current.forEach(clearTimeout);
    progressTimers.current = [];
  }, []);

  const handleSend = useCallback(
    async (rawText) => {
      const text = (rawText ?? input).trim();
      if (!text || busy) return;

      const userMessage = { id: nextId(), role: "user", text };
      const replyId = nextId();

      setMessages((prev) => [
        ...prev,
        userMessage,
        { id: replyId, role: "assistant", status: "pending", statusLabel: PROGRESS_STEPS[0].label, feedback: null },
      ]);
      setInput("");
      setBusy(true);
      startProgressNarration(replyId);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const data = await sendMessage({
          conversationID,
          practiceID,
          userID,
          message: text,
          pageContext,
          sessionContext,
          signal: controller.signal,
        });

        if (data?.ConversationID) setConversationID(data.ConversationID);
        patchMessage(replyId, { status: "complete", data });
      } catch (err) {
        if (err?.name === "AbortError") {
          patchMessage(replyId, { status: "error", text: "Stopped.", errorKind: "error" });
        } else {
          // 404 means the conversation vanished server-side - start a fresh one silently.
          if (err?.kind === "notFound") setConversationID(null);
          patchMessage(replyId, {
            status: "error",
            text: err?.message || "Something went wrong. Please try again.",
            errorKind: err?.kind || "error",
          });
        }
      } finally {
        stopProgressNarration();
        abortRef.current = null;
        setBusy(false);
      }
    },
    [
      input, busy, conversationID, practiceID, userID, pageContext, sessionContext,
      patchMessage, startProgressNarration, stopProgressNarration,
    ]
  );

  const handleStop = useCallback(() => abortRef.current?.abort(), []);

  /**
   * Reopens a past thread.
   *
   * GetConversation returns one row per message - ConversationMessageItem - not a row per
   * question-and-answer pair, and it names the fields sRole / sContent / iFeedbackRating.
   * Reading a Q+A shape here is what made "Pick up where you left off" silently restore
   * nothing: every row failed to match, `restored` stayed empty, and the click looked like
   * a dead button rather than a failure anyone would report.
   *
   * Assistant rows are reshaped into the ChatAnswer shape MessageList already renders, so a
   * resumed answer keeps its citations, data sources and deep links instead of degrading to
   * bare text. Alternate spellings are still tolerated so a frontend deployed ahead of the
   * service degrades rather than breaks.
   */
  const handleResume = useCallback(
    async (topic) => {
      if (busy) return;
      setBusy(true);
      try {
        const data = await getConversation({
          conversationID: topic.conversationID,
          practiceID,
          userID,
        });

        const rows = data?.Messages || data?.messages || [];
        const restored = [];

        rows.forEach((row) => {
          const role = String(row.sRole ?? row.Role ?? "").toLowerCase();
          const content = row.sContent ?? row.sMessage ?? row.sAnswer ?? "";
          if (!content) return;

          if (role === "user") {
            restored.push({ id: nextId(), role: "user", text: content });
            return;
          }

          // Only 1 and -1 are real ratings - the query returns 0 for "never rated", and
          // passing that straight through would light up neither thumb yet still count as
          // a prior vote, so the first click would read as a toggle-off and post nothing.
          const rating = Number(row.iFeedbackRating ?? row.iRating ?? 0);

          restored.push({
            id: nextId(),
            role: "assistant",
            status: "complete",
            data: {
              ...row,
              sAnswer: content,
              Citations: row.Citations || [],
              DataSources: row.DataSources || [],
              DeepLinks: row.DeepLinks || [],
            },
            feedback: rating === 1 || rating === -1 ? rating : null,
          });
        });

        if (restored.length) {
          setMessages(restored);
          setConversationID(data.ConversationID ?? topic.conversationID);
        }
      } catch {
        /* Stay on the empty state - the starter prompts are still there. */
      } finally {
        setBusy(false);
      }
    },
    [busy, practiceID, userID]
  );

  const handleFeedback = useCallback(
    (message, rating) => {
      const next = message.feedback === rating ? null : rating;
      patchMessage(message.id, { feedback: next });
      if (next) {
        saveFeedback({
          conversationID,
          messageID: message.data?.MessageID,
          practiceID,
          userID,
          rating: next,
        });
      }
    },
    [conversationID, practiceID, userID, patchMessage]
  );

  if (!userData) return null;
  if (HIDDEN_ROUTES.some((re) => re.test(pathname))) return null;

  const contextLabel = [
    pageContext.sScreenName,
    pageContext.ClaimID ? `Claim ${pageContext.ClaimID}` : null,
    !pageContext.ClaimID && pageContext.sMRN ? `MRN ${pageContext.sMRN}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="remi-root">
      {!open ? (
        <div className="remi-launcher-wrap">
          <span className="remi-launcher-label">Ask Remi</span>
          <button
            ref={launcherRef}
            type="button"
            className="remi-launcher"
            onClick={() => setOpen(true)}
            aria-label="Open Remi, the billing assistant"
            aria-expanded="false"
            title="Ask Remi"
          >
            <RemiMark size={26} />
          </button>
        </div>
      ) : (
        <div
          className="remi-panel"
          ref={panelRef}
          tabIndex={-1}
          role="dialog"
          aria-label="Remi - Billing Assistant"
        >
          <div className="remi-header">
            <span className="remi-header-mark">
              <RemiMark size={18} />
            </span>
            <div className="remi-header-text">
              <div className="remi-header-name">Remi</div>
              <div className="remi-header-sub">Billing Assistant</div>
            </div>
            <button
              type="button"
              className="remi-header-btn"
              onClick={() => setOpen(false)}
              aria-label="Minimise Remi"
              title="Minimise"
            >
              <ChevronDownIcon />
            </button>
            <button
              type="button"
              className="remi-header-btn"
              onClick={() => {
                abortRef.current?.abort();
                setMessages([]);
                setConversationID(null);
                setOpen(false);
              }}
              aria-label="Close Remi and clear the conversation"
              title="Close and clear"
            >
              <CloseIcon />
            </button>
          </div>

          <div className="remi-body" ref={bodyRef}>
            {contextLabel && (
              <div className="remi-context-chip">
                <span className="remi-tool-dot" />
                {contextLabel}
              </div>
            )}

            {messages.length === 0 ? (
              <>
                <div className="remi-greeting">
                  {firstName ? `Hi ${firstName} - ` : "Hi - "}
                  <strong>I'm Remi.</strong> I can look up live claim data, explain coding and
                  denial codes, and show you where to do things in the portal.
                </div>
                {/* Only claim personalisation when ChatMS actually reported it.
                    Below its confidence threshold this is the plain static list, and
                    saying otherwise would be a small lie the biller can catch. */}
                <div className="remi-suggest-label">
                  {isPersonalised ? "Based on your work" : "Try asking"}
                </div>
                <div className="remi-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.text}
                      type="button"
                      className="remi-suggestion"
                      onClick={() => handleSend(s.text)}
                    >
                      {s.text}
                      {s.reason && <span className="remi-suggestion-reason">{s.reason}</span>}
                    </button>
                  ))}
                </div>

                {recentTopics.length > 0 && (
                  <>
                    <div className="remi-suggest-label remi-recent-label">Pick up where you left off</div>
                    <div className="remi-recents">
                      {recentTopics.map((t) => (
                        <button
                          key={t.conversationID}
                          type="button"
                          className="remi-recent"
                          onClick={() => handleResume(t)}
                          title={t.title}
                        >
                          {t.title}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <MessageList messages={messages} onFeedback={handleFeedback} />
            )}
          </div>

          <Composer
            value={input}
            onChange={setInput}
            onSubmit={() => handleSend()}
            onStop={handleStop}
            busy={busy}
          />

          <div className="remi-footnote">
            Read-only. Remi can explain and link, but can't approve, post or resubmit.
          </div>
        </div>
      )}
    </div>
  );
}
